import moment from 'moment';
import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { ACalendar, Input, Label } from 'components/atoms';
import PositionSyncAlert, {
  PositionSyncAlertVariant,
} from 'components/molecules/PositionSyncAlert/PositionSyncAlert';
import useCalendar from 'hooks/useCalendar';
import { PositionSyncMessages, usePositionSync } from 'hooks/usePositionSync';

import { IAdminPanelForm } from '../AdminPanelForm.schema';
import OrganizationSection from '../OrganizationSection';
import { useLaboralSubmit } from '../useLaboralSubmit';
import { useOrganizationalSubmit } from '../useOrganizationalSubmit';

import ContractTypeSelect from './ContractTypeSelect';

import styles from './EmploymentSection.module.scss';

const ALERT_TITLES: Record<PositionSyncAlertVariant, string> = {
  info: 'Agrega el ID de la posición',
  loading: 'Sincronizando datos de la posición desde SAP...',
  error: 'No pudimos cargar los datos de la posición',
};

export default function EmploymentSection() {
  const { control, setValue, watch } = useFormContext<IAdminPanelForm>();
  const submitData = useLaboralSubmit();
  const submitOrganizational = useOrganizationalSubmit();
  const startDateCalendar = useCalendar();
  const fechaAprCalendar = useCalendar();
  const contractType = watch('contractType');
  const positionValue = watch('position');

  const { status, errorMessage, syncPosition } = usePositionSync();
  const [isAlertDismissed, setIsAlertDismissed] = useState(false);

 
  const areOptionalFieldsEnabled =
    status === 'success' || (status === 'idle' && Boolean(positionValue));

  const alertVariant: PositionSyncAlertVariant =
    status === 'loading' ? 'loading' : status === 'error' ? 'error' : 'info';

  const alertMessage =
    status === 'loading'
      ? PositionSyncMessages.loading
      : status === 'error'
      ? errorMessage
      : PositionSyncMessages.idle;

  async function handlePositionBlur(code: string) {
    setIsAlertDismissed(false);
    const synced = await syncPosition(code);
    if (!synced) return;

    await submitOrganizational();
    await submitData();
  }

  return (
    <div className={styles.EmploymentSection}>
      <h2 className={styles.EmploymentSection__Title}>Información laboral</h2>

      {!isAlertDismissed && (
        <PositionSyncAlert
          variant={alertVariant}
          title={ALERT_TITLES[alertVariant]}
          message={alertMessage}
          onClose={() => setIsAlertDismissed(true)}
        />
      )}

      <div className={styles.EmploymentSection__Grid}>
        <Controller
          control={control}
          name='contractType'
          render={({ field: { onChange, value, name, ...field }, fieldState: { error } }) => {
            return (
              <ContractTypeSelect
                value={{
                  value,
                  label: value,
                }}
                handleChange={({ value, valueEn, externalCode }) => {
                  if (typeof value === 'number') return;

                  setValue('organizationalInformation.contractTypeEn', valueEn);
                  setValue(
                    'organizationalInformation.contractTypeCountry',
                    externalCode.split('-')[0]
                  );

                  if (value === 'Aprendizaje Etapa Lectiva-COL') {
                    setValue('organizationalInformation.aprStartDate', null);
                  }

                  onChange(value);
                  submitData();
                }}
                error={error !== undefined}
                textLabel='Tipo de contrato'
                htmlForLabel='Tipo de contrato'
                subtext={error?.message}
                name={name}
                {...field}
              />
            );
          }}
        />
        <div className={styles.startDateContract}>
          <Label text='Fecha de inicio de contrato' htmlFor='startDate' />
          <Controller
            control={control}
            name='organizationalInformation.startDate'
            rules={{ required: true }}
            render={({ field: { onChange, onBlur, value }, fieldState }) => (
              <ACalendar
                birthdateForm={moment(value)}
                updateBirthdate={(value) => {
                  onChange(value);
                  onBlur();
                  submitData();
                }}
                setMarginForCalendar={startDateCalendar.setMarginForCalendar}
                isOpenCalendar={startDateCalendar.isCalendarOpen}
                setIsOpenCalendar={startDateCalendar.setIsCalendarOpen}
                error={fieldState.error !== undefined}
                allowFutureDates={true}
              />
            )}
          />
        </div>
        <Controller
          control={control}
          name='position'
          render={({ field: { onChange, onBlur, value, ...rest }, fieldState }) => (
            <Input
              id='position'
              label='ID de la Posición'
              error={fieldState.error !== undefined || status === 'error'}
              success={status === 'success'}
              subtext={fieldState.error?.message}
              onChange={(e) => {
                onChange(e.target.value);
              }}
              onBlur={() => {
                onBlur();
                handlePositionBlur(value);
              }}
              value={value}
              {...rest}
            />
          )}
        />
        <Controller
          control={control}
          name='role'
          render={({ field: { value, ...rest }, fieldState }) => (
            <Input
              id='role'
              label='Rol'
              error={fieldState.error !== undefined}
              subtext={fieldState.error?.message}
              value={value}
              disabled
              {...rest}
            />
          )}
        />

        {contractType === 'Aprendizaje Etapa Lectiva-COL' && (
          <div>
            <Label text='Fecha de inicio productiva' htmlFor='aprStartDate' />
            <Controller
              control={control}
              name='organizationalInformation.aprStartDate'
              rules={{ required: true }}
              render={({ field: { onChange, onBlur, value }, fieldState }) => (
                <ACalendar
                  birthdateForm={value ? moment(value) : undefined}
                  updateBirthdate={(value) => {
                    onChange(value);
                    onBlur();
                    submitData();
                  }}
                  setMarginForCalendar={fechaAprCalendar.setMarginForCalendar}
                  isOpenCalendar={fechaAprCalendar.isCalendarOpen}
                  setIsOpenCalendar={fechaAprCalendar.setIsCalendarOpen}
                  error={fieldState.error !== undefined}
                  allowFutureDates={true}
                />
              )}
            />
          </div>
        )}
      </div>

      <OrganizationSection isLocked areOptionalFieldsEnabled={areOptionalFieldsEnabled} />
    </div>
  );
}
